import { useMemo } from 'react'
import { traceColor, type LapTrace } from '../analysis/laps'
import { CORNER_LEAN_DEG, sectionStats, type Section, type SectionStats, type StatChannels } from '../analysis/sections'

interface Props {
  traces: LapTrace[]
  refTrace: LapTrace
  sections: Section[]
  channels: StatChannels & { speedUnit?: string }
  /** Section under the anchor, highlighted; -1 for none */
  active: number
  /** Why there are no sections, shown instead of the table */
  note?: string
  /** Pointer over a section's row or line (null when it leaves): the anchor follows, as on the charts */
  onHover?: (section: Section | null) => void
  /** Click on a section's row or line: pin the anchor there */
  onPin?: (section: Section) => void
}

/** Differences smaller than these are shown in the plain colour: not better, not worse. */
const TIME_EPS_S = 0.2
const SPEED_EPS = 2
const LEAN_EPS_DEG = 2

/**
 * Per-section comparison of every selected lap with the reference: time lost or gained,
 * minimum speed and the largest lean on each side. Clearly better than the reference is
 * green, clearly worse is red.
 */
export default function SectionSummary({ traces, refTrace, sections, channels, active, note, onHover, onPin }: Props) {
  const stats = useMemo(() => traces.map((t) => sectionStats(t, sections, channels)), [traces, sections, channels])

  if (sections.length === 0) {
    return (
      <section className="summary panel-block">
        <h3>Summary</h3>
        <p className="muted small">{note ?? 'No sections are recorded for this track yet.'}</p>
      </section>
    )
  }

  const refIdx = traces.indexOf(refTrace)
  const refStats = stats[refIdx]
  const others = traces.map((_, i) => i).filter((i) => i !== refIdx)
  const hasSpeed = !!channels.speed
  const hasLean = !!channels.lean
  // A side is listed when some section corners that way; a cell stays empty where it does not.
  const corner = (st: SectionStats, side: 'leanLeft' | 'leanRight') => st[side] >= CORNER_LEAN_DEG
  const showLeft = hasLean && refStats.some((st) => corner(st, 'leanLeft'))
  const showRight = hasLean && refStats.some((st) => corner(st, 'leanRight'))
  const perLap = 1 + (hasSpeed ? 1 : 0) + (showLeft ? 1 : 0) + (showRight ? 1 : 0)

  return (
    <section className="summary panel-block">
      <h3>
        Summary{' '}
        <small className="muted">
          by section, vs <span className="swatch" style={{ background: traceColor(refTrace, refIdx) }} />
          {refTrace.label}
        </small>
      </h3>
      {others.length === 0 && <p className="muted small">Select another lap to compare it with the reference.</p>}
      {others.length > 0 && (
        <div className="summary-text">
          {others.map((i) => {
            const lines = refStats
              .map((rs, row) => ({
                name: rs.section.name,
                row,
                section: rs.section,
                lost: stats[i][row].time - rs.time,
                parts: findings(stats[i][row], rs, {
                  speed: hasSpeed,
                  left: showLeft && corner(rs, 'leanLeft'),
                  right: showRight && corner(rs, 'leanRight'),
                  speedUnit: channels.speedUnit,
                }),
              }))
              // Only sections where time was clearly lost or gained are worth a line.
              .filter((l) => Math.abs(l.lost) > TIME_EPS_S)
            // The section that costs the most time stands out.
            const worst = lines.reduce<(typeof lines)[number] | null>(
              (w, l) => (l.lost > 0 && (!w || l.lost > w.lost) ? l : w),
              null,
            )
            return (
              <div key={traces[i].key} className="summary-lap">
                {others.length > 1 && (
                  <p className="summary-lap-name">
                    <span className="swatch" style={{ background: traceColor(traces[i], i) }} />
                    {traces[i].label}
                  </p>
                )}
                {lines.length === 0 ? (
                  <p className="muted small">No section is clearly faster or slower than {refTrace.label}.</p>
                ) : (
                  <ul onMouseLeave={() => onHover?.(null)}>
                    {lines.map((l) => (
                      <li
                        key={l.name}
                        className={`linked${l.row === active ? ' active' : ''}${l === worst ? ' worst' : ''}`}
                        onMouseEnter={() => onHover?.(l.section)}
                        onClick={() => onPin?.(l.section)}
                      >
                        <span className="section-name">{l.name}</span>:{' '}
                        {l.parts.map((part, k) => (
                          <span key={k}>
                            {k > 0 && ', '}
                            <span className={part.cls}>{part.text}</span>
                          </span>
                        ))}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}
      {others.length > 0 && (
        <div className="table-wrap">
          <table className="laps summary-table" onMouseLeave={() => onHover?.(null)}>
            <thead>
              <tr>
                <th rowSpan={2} className="section-col">
                  Section
                </th>
                {others.map((i) => (
                  <th key={traces[i].key} colSpan={perLap} className="lap-col">
                    <span className="swatch" style={{ background: traceColor(traces[i], i) }} />
                    {traces[i].label}
                  </th>
                ))}
              </tr>
              <tr className="metrics">
                {others.map((i) => (
                  <MetricHeadDelta key={traces[i].key} hasSpeed={hasSpeed} showLeft={showLeft} showRight={showRight} />
                ))}
              </tr>
            </thead>
            <tbody>
              {refStats.map((rs, row) => {
                const left = corner(rs, 'leanLeft')
                const right = corner(rs, 'leanRight')
                return (
                  <tr
                    key={row}
                    className={`linked${row === active ? ' active' : ''}`}
                    onMouseEnter={() => onHover?.(rs.section)}
                    onClick={() => onPin?.(rs.section)}
                  >
                    <td className="section-col">
                      <span className="section-name">{rs.section.name}</span>
                      <small className="muted">
                        {' '}
                        {Math.round(rs.section.from)}–{Math.round(rs.section.to)} m
                      </small>
                    </td>
                    {others.map((i) => {
                      const st = stats[i][row]
                      return (
                        <Cells
                          key={traces[i].key}
                          st={st}
                          ref={rs}
                          hasSpeed={hasSpeed}
                          left={showLeft ? left : false}
                          right={showRight ? right : false}
                          showLeft={showLeft}
                          showRight={showRight}
                          speedUnit={channels.speedUnit}
                        />
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function MetricHeadDelta({
  hasSpeed,
  showLeft,
  showRight,
}: {
  hasSpeed: boolean
  showLeft: boolean
  showRight: boolean
}) {
  return (
    <>
      <th title="Time lost (+) or gained (−) against the reference">Δ time</th>
      {hasSpeed && <th title="Lowest speed in the section, and the difference to the reference">min speed</th>}
      {showLeft && <th title="Largest lean to the left, and the difference to the reference">lean L</th>}
      {showRight && <th title="Largest lean to the right, and the difference to the reference">lean R</th>}
    </>
  )
}

function Cells({
  st,
  ref,
  hasSpeed,
  left,
  right,
  showLeft,
  showRight,
  speedUnit,
}: {
  st: SectionStats
  ref: SectionStats
  hasSpeed: boolean
  left: boolean
  right: boolean
  showLeft: boolean
  showRight: boolean
  speedUnit?: string
}) {
  const dt = st.time - ref.time
  return (
    <>
      <td className={grade(-dt, TIME_EPS_S)}>{fmtDelta(dt, 3, 's')}</td>
      {hasSpeed && (
        <td className={grade(st.minSpeed - ref.minSpeed, SPEED_EPS)}>
          {fmtNum(st.minSpeed, 1, speedUnit)} <small>({fmtDelta(st.minSpeed - ref.minSpeed, 1)})</small>
        </td>
      )}
      {showLeft && (
        <td className={left ? grade(st.leanLeft - ref.leanLeft, LEAN_EPS_DEG) : ''}>
          {left ? (
            <>
              {fmtNum(st.leanLeft, 0, '°')} <small>({fmtDelta(st.leanLeft - ref.leanLeft, 1)})</small>
            </>
          ) : (
            ''
          )}
        </td>
      )}
      {showRight && (
        <td className={right ? grade(st.leanRight - ref.leanRight, LEAN_EPS_DEG) : ''}>
          {right ? (
            <>
              {fmtNum(st.leanRight, 0, '°')} <small>({fmtDelta(st.leanRight - ref.leanRight, 1)})</small>
            </>
          ) : (
            ''
          )}
        </td>
      )}
    </>
  )
}

/** The clear differences in one section, as short phrases: only what is graded in the table. */
function findings(
  st: SectionStats,
  ref: SectionStats,
  show: { speed: boolean; left: boolean; right: boolean; speedUnit?: string },
): { cls: string; text: string }[] {
  const out: { cls: string; text: string }[] = []
  const dt = st.time - ref.time
  const gt = grade(-dt, TIME_EPS_S)
  if (gt) out.push({ cls: gt, text: `${gt === 'good' ? 'gained' : 'lost'} ${Math.abs(dt).toFixed(3)} s` })
  if (show.speed) {
    const dv = st.minSpeed - ref.minSpeed
    const g = grade(dv, SPEED_EPS)
    if (g)
      out.push({
        cls: g,
        text: `min speed ${Math.abs(dv).toFixed(1)} ${show.speedUnit ?? ''} ${g === 'good' ? 'higher' : 'lower'}`.replace(
          '  ',
          ' ',
        ),
      })
  }
  for (const [on, key, side] of [
    [show.left, 'leanLeft', 'left'],
    [show.right, 'leanRight', 'right'],
  ] as const) {
    if (!on) continue
    const d = st[key] - ref[key]
    const g = grade(d, LEAN_EPS_DEG)
    if (g)
      out.push({ cls: g, text: `${Math.abs(d).toFixed(1)}° ${g === 'good' ? 'more' : 'less'} lean to the ${side}` })
  }
  return out
}

/** Class for a comparison: positive `better` is good, negative is bad, tiny differences neutral. */
function grade(better: number, eps: number): string {
  if (!Number.isFinite(better) || Math.abs(better) <= eps) return ''
  return better > 0 ? 'good' : 'bad'
}

function fmtNum(v: number, decimals: number, unit?: string): string {
  if (!Number.isFinite(v)) return '–'
  return `${v.toFixed(decimals)}${unit ? (unit === '°' ? unit : ' ' + unit) : ''}`
}

function fmtDelta(v: number, decimals: number, unit?: string): string {
  if (!Number.isFinite(v)) return '–'
  const sign = v > 0 ? '+' : v < 0 ? '−' : '±'
  return `${sign}${Math.abs(v).toFixed(decimals)}${unit ? ' ' + unit : ''}`
}
